import React, { useState } from 'react';
import { AlertTriangle, Download } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { downloadCsv } from './sales/format';

/*
 * Preview-then-confirm step for uploads. Every backend import that supports
 * `?dry_run=true` returns this shape without writing anything:
 *
 *   {
 *     dry_run: true,
 *     summary: { create: n, update: n, unchanged: n, errors: n },
 *     rows: [{ key: "SKU123", action: "create"|"update"|"unchanged"|"error", detail: "warehouse 10 → 25" }],
 *     errors: ["Row 7: ..."],
 *     unmatched: ["CODE1", ...]        // optional
 *   }
 *
 * The caller re-sends the same file without dry_run inside onConfirm.
 */
const ACTION_STYLES = {
  create: 'bg-green-50 text-green-700',
  update: 'bg-blue-50 text-blue-700',
  unchanged: 'bg-gray-100 text-gray-600',
  error: 'bg-red-50 text-red-700',
};
const ACTION_LABELS = { create: 'New', update: 'Update', unchanged: 'No change', error: 'Error' };
const MAX_ROWS = 200;

export const ImportPreviewDialog = ({
  open,
  onOpenChange,
  title = 'Review import',
  fileName,
  preview,
  entityLabel = 'rows',
  confirmLabel = 'Confirm import',
  onConfirm,
  issuesNote,
  testId = 'import-preview',
}) => {
  const [busy, setBusy] = useState(false);
  const summary = preview?.summary || {};
  const rows = preview?.rows || [];
  const errors = preview?.errors || [];
  // Codes already named in an error line are not listed (or counted) twice.
  const unmatched = (preview?.unmatched || []).filter((code) => !errors.some((e) => e.includes(`'${code}'`)));
  const changes = (summary.create || 0) + (summary.update || 0);

  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm?.();
      onOpenChange(false);
    } catch {
      // Caller shows the error; keep the preview open.
    } finally {
      setBusy(false);
    }
  };

  const exportIssues = () => downloadCsv(
    [['Issue'], ...errors.map((e) => [e]), ...unmatched.map((code) => [`Unmatched code: ${code}`])],
    'import-issues.csv',
  );

  return (
    <Dialog open={open} onOpenChange={(value) => { if (!busy) onOpenChange(value); }}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid={testId}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {fileName ? <><span className="font-medium">{fileName}</span>. </> : null}
            Nothing has been saved yet. Check the changes below, then confirm.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-2 text-sm" data-testid={`${testId}-summary`}>
          {['create', 'update', 'unchanged', 'errors'].map((k) => (
            <span key={k} className={`rounded-full px-3 py-1 font-medium ${ACTION_STYLES[k === 'errors' ? 'error' : k]}`}>
              {summary[k] || 0} {k === 'errors' ? 'with errors' : ACTION_LABELS[k].toLowerCase()}
            </span>
          ))}
        </div>

        {(errors.length > 0 || unmatched.length > 0) && (
          <div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid={`${testId}-issues`}>
            <div className="flex items-start justify-between gap-3">
              <p className="flex items-center gap-1.5 font-medium">
                <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                {errors.length + unmatched.length} {errors.length + unmatched.length === 1 ? 'issue' : 'issues'}. {issuesNote || `These ${entityLabel} will be skipped.`}
              </p>
              <Button size="sm" variant="outline" onClick={exportIssues} data-testid={`${testId}-download-issues`}>
                <Download className="mr-1.5 h-4 w-4" aria-hidden="true" /> Download
              </Button>
            </div>
            <ul className="mt-2 max-h-32 list-disc space-y-0.5 overflow-y-auto pl-5">
              {errors.slice(0, 50).map((e, i) => <li key={`e${i}`}>{e}</li>)}
              {unmatched.slice(0, 50).map((code) => <li key={`u${code}`}>Code not found: {code}</li>)}
            </ul>
            {errors.length + unmatched.length > 100 && <p className="mt-1 text-xs">Showing the first 100. Download for the full list.</p>}
          </div>
        )}

        {rows.length > 0 && (
          <div className="max-h-72 overflow-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 text-left text-xs uppercase text-gray-600">
                <tr><th className="px-3 py-2">Item</th><th className="px-3 py-2">Change</th><th className="px-3 py-2">Details</th></tr>
              </thead>
              <tbody>
                {rows.slice(0, MAX_ROWS).map((row, i) => (
                  <tr key={`${row.key}-${i}`} className="border-t">
                    <td className="px-3 py-1.5 font-mono text-xs">{row.key}</td>
                    <td className="px-3 py-1.5">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${ACTION_STYLES[row.action] || ACTION_STYLES.unchanged}`}>
                        {ACTION_LABELS[row.action] || row.action}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-gray-700">{row.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length > MAX_ROWS && <p className="border-t px-3 py-2 text-xs text-gray-500">Showing {MAX_ROWS} of {rows.length} {entityLabel}.</p>}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" disabled={busy} onClick={() => onOpenChange(false)} data-testid={`${testId}-cancel`}>Cancel</Button>
          <Button disabled={busy || changes === 0} onClick={confirm} data-testid={`${testId}-confirm`}>
            {busy ? 'Importing…' : changes === 0 ? 'Nothing to import' : `${confirmLabel} (${changes})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportPreviewDialog;
